import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Chip,
  IconButton,
  Alert,
  Avatar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Switch,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoCameraIcon,
  Upload as UploadIcon,
  CameraAlt as CameraAltIcon,
  SwapHoriz as SwapHorizIcon,
  History as HistoryIcon,
  AccountCircle as AccountLinkedIcon,
  PersonOff as NoAccountIcon,
  Block as BlockIcon,
  Check as CheckIcon,
  HowToReg as HowToRegIcon,
  ManageAccounts as ImpersonateIcon,
  CompareArrows as MergePlayersIcon,
  Wifi as IpIcon,
} from "@mui/icons-material";
import {
  Player,
  Division,
  SPL_DIVISIONS,
  PADEL_EAGLE_DIVISIONS,
} from "../types";
import { apiRequest } from "../config/api";
import ImageUploadService from "../services/imageUpload";
import { useDivision } from "../contexts/DivisionContext";
import { useSeason } from "../contexts/SeasonContext";
import { useOrganization } from "../contexts/OrganizationContext";
import { useAuth } from "../contexts/AuthContext";
import DivisionSelector from "../components/DivisionSelector";
import SafeAvatar from "../components/SafeAvatar";
import BadgeDisplay from "../components/BadgeDisplay";
import MergePlayersDialog from "../components/MergePlayersDialog";

function Players() {
  const { currentDivision } = useDivision();
  const { allSeasons, activeSeason } = useSeason();
  const { currentOrg } = useOrganization();
  const { impersonatePlayer } = useAuth();
  const [players, setPlayers] = useState<Player[]>([]);
  const [seasonFilter, setSeasonFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    profilePicture: "",
    division: currentDivision as Division,
    level: 3.0, // For tournament orgs
    trialDaysCount: 0,
    isGuest: false,
    isActive: true,
    isManagementTeam: false,
    autoRegisterLeagueDay: false,
    allowedDivisions: [] as string[],
    adminNote: "", // Admin note for this player
  });

  const isTournamentOrg =
    currentOrg?.competitionType === "tournament" ||
    currentOrg?.competitionType === "both";
  const isLeagueOrg =
    currentOrg?.competitionType === "league" ||
    currentOrg?.competitionType === "both";
  const [error, setError] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [photoMenuAnchor, setPhotoMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Move player dialog state
  const [movePlayerDialogOpen, setMovePlayerDialogOpen] = useState(false);
  const [playerToMove, setPlayerToMove] = useState<Player | null>(null);
  const [targetDivision, setTargetDivision] = useState<Division | "">("");
  const [moveReason, setMoveReason] = useState("");
  const [moveError, setMoveError] = useState("");
  const [moveLoading, setMoveLoading] = useState(false);

  // SP Team badge state
  const [hasSpTeamBadge, setHasSpTeamBadge] = useState(false);

  // Ban dialog state
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [playerToBan, setPlayerToBan] = useState<Player | null>(null);
  const [banReason, setBanReason] = useState("");
  const [banLoading, setBanLoading] = useState(false);

  // Approve for season dialog state
  const [approveSeasonDialogOpen, setApproveSeasonDialogOpen] = useState(false);
  const [playerToApprove, setPlayerToApprove] = useState<Player | null>(null);
  const [approveSeasonId, setApproveSeasonId] = useState("");
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState("");
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [playerToMerge, setPlayerToMerge] = useState<Player | null>(null);

  // IP block management
  const [ipDialogOpen, setIpDialogOpen] = useState(false);
  const [playerForIps, setPlayerForIps] = useState<Player | null>(null);
  const [blockingIp, setBlockingIp] = useState<string | null>(null);
  const [autoBlockLoading, setAutoBlockLoading] = useState(false);

  useEffect(() => {
    fetchPlayers();
  }, [currentDivision, searchQuery]);

  // Add global paste event listener when dialog is open
  useEffect(() => {
    if (!dialogOpen) return;

    const handleGlobalPaste = (event: Event) => {
      const clipboardEvent = event as ClipboardEvent;

      // Check if we're not focused on a text input
      const activeElement = document.activeElement;
      const isTextField =
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA";

      if (isTextField) return; // Let text fields handle their own paste

      const items = clipboardEvent.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.type.startsWith("image/")) {
          clipboardEvent.preventDefault();

          const file = item.getAsFile();
          if (file) {
            if (file.size > 2 * 1024 * 1024) {
              setError("Image file must be less than 2MB");
              return;
            }

            if (imageFile) {
              URL.revokeObjectURL(URL.createObjectURL(imageFile));
            }

            setImageFile(file);
            setError("");
            break;
          }
        }
      }
    };

    window.addEventListener("paste", handleGlobalPaste);

    return () => {
      window.removeEventListener("paste", handleGlobalPaste);
    };
  }, [dialogOpen, imageFile]);

  const fetchPlayers = async () => {
    try {
      // Search spans all divisions; otherwise filter by selected division
      const isUnassignedDivisionSelected = currentDivision === "unassigned";
      const url = searchQuery || isUnassignedDivisionSelected
        ? `/api/players`
        : `/api/players?division=${currentDivision}`;
      const response = await apiRequest(url);
      if (response.ok) {
        const data = await response.json();
        // Convert Firebase Timestamp objects to Date objects in divisionHistory
        const playersWithDates = data.map((player: Player) => ({
          ...player,
          divisionHistory: player.divisionHistory?.map((movement) => {
            // Handle Firebase Timestamp format with _seconds
            let movedAtDate: Date;
            if (
              typeof movement.movedAt === "object" &&
              movement.movedAt !== null
            ) {
              const timestamp = movement.movedAt as any;
              if (timestamp._seconds) {
                movedAtDate = new Date(timestamp._seconds * 1000);
              } else if (timestamp.seconds) {
                movedAtDate = new Date(timestamp.seconds * 1000);
              } else {
                movedAtDate = new Date(movement.movedAt);
              }
            } else {
              movedAtDate = new Date(movement.movedAt);
            }

            return {
              ...movement,
              movedAt: movedAtDate,
            };
          }),
        }));
        const visiblePlayers = isUnassignedDivisionSelected
          ? playersWithDates.filter((player: Player) => {
              const division = (player.division || "").trim().toLowerCase();
              return !division || division === "unassigned" || !!player.isTournamentOnly;
            })
          : playersWithDates;
        setPlayers(visiblePlayers);
      }
    } catch (error) {
      console.error("Error fetching players:", error);
      setError("Failed to fetch players");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlayer = () => {
    setEditingPlayer(null);
    setFormData({
      name: "",
      profilePicture: "",
      division: currentDivision as Division,
      level: 3.0,
      trialDaysCount: 0,
      isGuest: false,
      isActive: true,
      isManagementTeam: false,
      autoRegisterLeagueDay: false,
      allowedDivisions: [] as string[],
      adminNote: "",
    });
    setHasSpTeamBadge(false);
    setImageFile(null);
    setDialogOpen(true);
  };

  const handleEditPlayer = (player: Player) => {
    setEditingPlayer(player);
    setFormData({
      name: player.name,
      profilePicture: player.profilePicture || "",
      division: player.division,
      level: (player as any).level || 3.0,
      trialDaysCount: player.trialDaysCount || 0,
      isGuest: player.isGuest || false,
      isActive: player.isActive !== undefined ? player.isActive : true,
      isManagementTeam: player.isManagementTeam || false,
      autoRegisterLeagueDay: player.autoRegisterLeagueDay || false,
      allowedDivisions: player.allowedDivisions || [],
      adminNote: player.adminNote || "",
    });
    setHasSpTeamBadge(
      player.badges?.some((b) => b.type === "sp_team") || false,
    );
    setImageFile(null);
    setDialogOpen(true);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        // 2MB limit
        setError("Image file must be less than 2MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        return;
      }

      // Clean up previous object URL if exists
      if (imageFile) {
        URL.revokeObjectURL(URL.createObjectURL(imageFile));
      }

      setImageFile(file);
      setError("");
    }
  };

  const handlePhotoMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPhotoMenuAnchor(event.currentTarget);
  };

  const handlePhotoMenuClose = () => {
    setPhotoMenuAnchor(null);
  };

  const handleUploadFromDevice = () => {
    handlePhotoMenuClose();
    document.getElementById("profile-picture-upload")?.click();
  };

  const handleOpenCamera = async () => {
    handlePhotoMenuClose();
    setCameraDialogOpen(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 640, height: 480 },
      });
      setCameraStream(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      setError("Failed to access camera. Please check permissions.");
      setCameraDialogOpen(false);
    }
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          setImageFile(file);
          handleCloseCameraDialog();
        }
      },
      "image/jpeg",
      0.9,
    );
  };

  const handleCloseCameraDialog = () => {
    // Stop camera stream
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setCameraDialogOpen(false);
  };

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraStream]);

  const handleSavePlayer = async () => {
    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }

    try {
      setError("");
      let profilePictureUrl = formData.profilePicture;

      // Handle profile picture upload
      if (imageFile) {
        setImageUploading(true);
        try {
          profilePictureUrl = await ImageUploadService.uploadProfilePicture(
            imageFile,
            formData.name,
          );
        } catch (uploadError: any) {
          console.error("Image upload failed:", uploadError);
          const errorMessage = uploadError?.message || "Failed to upload image";
          setError(
            `Image Upload Error: ${errorMessage}. Please try again or contact support.`,
          );
          setImageUploading(false);
          return;
        }
        setImageUploading(false);
      }

      const method = editingPlayer ? "PUT" : "POST";
      const endpoint = editingPlayer
        ? `/api/players/${editingPlayer.id}`
        : "/api/players";

      const playerData: any = {
        name: formData.name.trim(),
        division: formData.division,
        trialDaysCount: formData.trialDaysCount || 0,
        isGuest: formData.isGuest || false,
        isActive: formData.isActive !== undefined ? formData.isActive : true,
        isManagementTeam: formData.isManagementTeam || false,
        autoRegisterLeagueDay: formData.autoRegisterLeagueDay || false,
        allowedDivisions: formData.allowedDivisions || [],
        adminNote: formData.adminNote.trim() || "",
      };

      // Set currentSeason when creating a new player so they appear in season-scoped views
      if (!editingPlayer && activeSeason?.id) {
        playerData.currentSeason = activeSeason.id;
      }

      // Add level for tournament organizations
      if (isTournamentOrg) {
        playerData.level = formData.level;
      }

      // Only add profilePicture if it has a value
      if (profilePictureUrl) {
        playerData.profilePicture = profilePictureUrl;
      }

      // Handle SP Team badge for editing players
      if (editingPlayer) {
        const currentHasBadge =
          editingPlayer.badges?.some((b) => b.type === "sp_team") || false;

        if (hasSpTeamBadge !== currentHasBadge) {
          // Badge status changed, need to update
          let badges = [...(editingPlayer.badges || [])];
          let unseenBadges = [...(editingPlayer.unseenBadges || [])];

          if (hasSpTeamBadge) {
            // Add SP Team badge
            badges.push({
              type: "sp_team",
              earnedAt: new Date(),
              metadata: {},
            });

            // Add to unseenBadges so player sees celebration animation
            if (!unseenBadges.includes("sp_team")) {
              unseenBadges.push("sp_team");
            }
          } else {
            // Remove SP Team badge
            badges = badges.filter((b) => b.type !== "sp_team");
            unseenBadges = unseenBadges.filter((type) => type !== "sp_team");
          }

          playerData.badges = badges;
          playerData.unseenBadges = unseenBadges;
        }
      }

      console.log("Saving player data:", playerData);

      // If we uploaded a new image and there was an old one, delete the old one
      if (editingPlayer && imageFile && editingPlayer.profilePicture) {
        try {
          await ImageUploadService.deleteProfilePicture(
            editingPlayer.profilePicture,
          );
        } catch (deleteError) {
          console.warn("Failed to delete old profile picture:", deleteError);
        }
      }

      const response = await apiRequest(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(playerData),
      });

      console.log("Server response:", response.status);

      if (response.ok) {
        setDialogOpen(false);
        setImageFile(null);
        setHasSpTeamBadge(false);
        setFormData({
          name: "",
          profilePicture: "",
          division: currentDivision as Division,
          level: 3.0,
          trialDaysCount: 0,
          isGuest: false,
          isActive: true,
          isManagementTeam: false,
          autoRegisterLeagueDay: false,
          adminNote: "",
        });
        fetchPlayers();
        setError("");
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to save player");
      }
    } catch (error) {
      console.error("Error saving player:", error);
      setError("Failed to save player");
      setImageUploading(false);
    }
  };

  const handleDeletePlayer = async (player: Player) => {
    if (!window.confirm(`Are you sure you want to delete ${player.name}?`)) {
      return;
    }

    try {
      const response = await apiRequest(`/api/players/${player.id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        // Delete profile picture if it exists and is a valid URL
        if (player.profilePicture && player.profilePicture.startsWith("http")) {
          try {
            await ImageUploadService.deleteProfilePicture(
              player.profilePicture,
            );
          } catch (deleteError) {
            console.warn("Failed to delete profile picture:", deleteError);
            // Continue anyway - player is already deleted from database
          }
        }

        fetchPlayers();
        setError("");
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to delete player");
      }
    } catch (error) {
      console.error("Error deleting player:", error);
      setError("Failed to delete player");
    }
  };

  const handleOpenMoveDialog = (player: Player) => {
    setPlayerToMove(player);
    setTargetDivision("");
    setMoveReason("");
    setMoveError("");
    setMovePlayerDialogOpen(true);
  };

  const handleMovePlayer = async () => {
    if (!playerToMove || !targetDivision) {
      setMoveError("Please select a target division");
      return;
    }

    setMoveLoading(true);
    setMoveError("");

    try {
      const response = await apiRequest(
        `/api/players/${playerToMove.id}/move-division`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            targetDivision,
            reason: moveReason.trim() || undefined,
          }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        setMovePlayerDialogOpen(false);
        fetchPlayers();
        setError("");
      } else {
        setMoveError(data.message || data.error || "Failed to move player");
      }
    } catch (error) {
      console.error("Error moving player:", error);
      setMoveError("Failed to move player");
    } finally {
      setMoveLoading(false);
    }
  };

  const handleOpenApproveSeasonDialog = (player: Player) => {
    setPlayerToApprove(player);
    setApproveSeasonId(activeSeason?.id || "");
    setApproveError("");
    setApproveSeasonDialogOpen(true);
  };

  const handleOpenMergeDialog = (player: Player) => {
    setPlayerToMerge(player);
    setMergeDialogOpen(true);
  };

  const handleMergeSuccess = () => {
    fetchPlayers();
    setError("");
  };

  const handleApproveForSeason = async () => {
    if (!playerToApprove || !approveSeasonId) return;
    setApproveLoading(true);
    setApproveError("");
    try {
      const response = await apiRequest("/api/season-registration/manual-approve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ playerId: playerToApprove.id, seasonId: approveSeasonId }),
      });
      const data = await response.json();
      if (response.ok) {
        setApproveSeasonDialogOpen(false);
        fetchPlayers();
      } else {
        setApproveError(data.error || "Failed to approve player for season");
      }
    } catch {
      setApproveError("Failed to approve player for season");
    } finally {
      setApproveLoading(false);
    }
  };

  const handleImpersonate = async (player: Player) => {
    if (!player.id) return;
    const result = await impersonatePlayer(player.id);
    if (result.success) {
      window.open("/player/dashboard", "_blank");
    } else {
      setError(result.error || "Failed to impersonate player");
    }
  };

  const handleBanToggle = (player: Player) => {
    setPlayerToBan(player);
    setBanReason("");
    setBanDialogOpen(true);
  };

  const handleOpenIpDialog = (player: Player) => {
    setPlayerForIps(player);
    setIpDialogOpen(true);
  };

  const handleBlockIp = async (ip: string) => {
    if (!playerForIps) return;
    setBlockingIp(ip);
    try {
      const response = await apiRequest("/api/admin/blocked-ips", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ip,
          reason: `Blocked from player record: ${playerForIps.name}`,
          playerId: playerForIps.id,
          playerName: playerForIps.name,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to block IP");
      }
    } catch (error) {
      console.error("Error blocking IP:", error);
      setError("Failed to block IP");
    } finally {
      setBlockingIp(null);
    }
  };

  const handleToggleAutoBlock = async () => {
    if (!playerForIps) return;
    const nextValue = !playerForIps.autoBlockIp;
    setAutoBlockLoading(true);
    try {
      const response = await apiRequest(
        `/api/players/${playerForIps.id}/auto-block-ip`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ autoBlockIp: nextValue }),
        }
      );

      if (response.ok) {
        setPlayerForIps({ ...playerForIps, autoBlockIp: nextValue });
        fetchPlayers();
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to update auto-block setting");
      }
    } catch (error) {
      console.error("Error updating auto-block setting:", error);
      setError("Failed to update auto-block setting");
    } finally {
      setAutoBlockLoading(false);
    }
  };

  const handleBanConfirm = async () => {
    if (!playerToBan) return;

    setBanLoading(true);
    try {
      const response = await apiRequest(`/api/players/${playerToBan.id}/ban`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          isBanned: !playerToBan.isBanned,
          bannedReason: playerToBan.isBanned
            ? undefined
            : banReason.trim() || undefined,
        }),
      });

      if (response.ok) {
        setBanDialogOpen(false);
        fetchPlayers();
        setError("");
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to update ban status");
      }
    } catch (error) {
      console.error("Error updating ban status:", error);
      setError("Failed to update ban status");
    } finally {
      setBanLoading(false);
    }
  };

  // Build the full division list for the "Move Division" dialog.
  // Priority: org config (Firestore) → org-specific constant (covers new divisions like 2A/2B/4)
  // Union that with every season's own division list so nothing ever goes missing.
  const baseDivisions: string[] = currentOrg?.divisions?.length
    ? currentOrg.divisions
    : currentOrg?.slug === "padel-eagle"
      ? PADEL_EAGLE_DIVISIONS
      : SPL_DIVISIONS;
  const allDivisionsSet = new Set<string>(baseDivisions);
  allDivisionsSet.add("unassigned");
  allDivisionsSet.add("Test");
  allSeasons.forEach((season) => {
    (season.divisions || []).forEach((d: string) => allDivisionsSet.add(d));
  });
  const availableDivisions = Array.from(allDivisionsSet).filter(
    (div) => div !== playerToMove?.division,
  ) as Division[];

  // Apply season filter
  const filteredPlayers = players.filter((p) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesName = p.name.toLowerCase().includes(q);
      const matchesUsername = p.username?.toLowerCase().includes(q);
      const matchesEmail = p.email?.toLowerCase().includes(q);
      const matchesPhone = p.phone?.toLowerCase().includes(q);
      if (!matchesName && !matchesUsername && !matchesEmail && !matchesPhone) return false;
    }
    if (seasonFilter === "active") {
      return activeSeason ? p.currentSeason === activeSeason.id : true;
    }
    if (seasonFilter === "inactive") {
      return (
        !!activeSeason &&
        !!p.currentSeason &&
        p.currentSeason !== activeSeason.id
      );
    }
    return true;
  });

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        mb={3}
        gap={{ xs: 2, sm: 0 }}
      >
        <Typography variant="h4">Player Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPlayer}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Add Player
        </Button>
      </Box>

      {/* Division Selector */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: "1px solid rgba(25, 118, 210, 0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              color: "#1976d2",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            🏆 Managing Division: {currentDivision}
          </Typography>
          <Box sx={{ minWidth: { xs: "100%", sm: 200 } }}>
            <DivisionSelector useAllDivisions />
          </Box>
        </Box>

        {/* Season membership filter */}
        {activeSeason && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Season:
            </Typography>
            {(
              [
                { value: "all", label: "All players" },
                { value: "active", label: `Approved for ${activeSeason.name}` },
                { value: "inactive", label: "Not yet in new season" },
              ] as const
            ).map(({ value, label }) => (
              <Chip
                key={value}
                label={label}
                onClick={() => setSeasonFilter(value)}
                color={seasonFilter === value ? "primary" : "default"}
                variant={seasonFilter === value ? "filled" : "outlined"}
                size="small"
              />
            ))}
          </Box>
        )}

        {/* Player search */}
        <Box sx={{ mt: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name, username or email…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <Box
                  component="span"
                  sx={{ mr: 1, color: "text.secondary", display: "flex" }}
                >
                  🔍
                </Box>
              ),
              endAdornment: searchQuery ? (
                <IconButton size="small" onClick={() => setSearchQuery("")}>
                  ✕
                </IconButton>
              ) : null,
            }}
          />
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Mobile hint */}
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          mb: 1,
          p: 1,
          bgcolor: "info.light",
          borderRadius: 1,
          color: "info.contrastText",
        }}
      >
        <Typography variant="caption">
          💡 Swipe horizontally to view all columns. Player names stay visible
          while scrolling.
        </Typography>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          overflow: "auto",
          maxHeight: { xs: 400, sm: 600, md: "none" },
        }}
      >
        <Table
          sx={{
            minWidth: { xs: 600, sm: 750 },
            "& .MuiTableCell-root": {
              padding: { xs: "4px 8px", sm: "8px 16px" },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: "background.paper",
                  zIndex: 1,
                  minWidth: { xs: 140, sm: 180 },
                  maxWidth: { xs: 140, sm: 200 },
                  boxShadow: "2px 0 5px rgba(0,0,0,0.1)",
                }}
              >
                Player
              </TableCell>
              {isTournamentOrg && (
                <TableCell sx={{ minWidth: { xs: 80, sm: 100 } }}>
                  Level
                </TableCell>
              )}
              {isLeagueOrg && (
                <TableCell sx={{ minWidth: { xs: 100, sm: 120 } }}>
                  Division
                </TableCell>
              )}
              {isLeagueOrg && (
                <TableCell
                  align="center"
                  sx={{ minWidth: { xs: 120, sm: 200 } }}
                >
                  <Box sx={{ display: { xs: "none", sm: "block" } }}>
                    Division Points
                  </Box>
                  <Box sx={{ display: { xs: "block", sm: "none" } }}>
                    Div Pts
                  </Box>
                </TableCell>
              )}
              {isLeagueOrg && (
                <TableCell
                  align="center"
                  sx={{ minWidth: { xs: 80, sm: 100 } }}
                >
                  <Box sx={{ display: { xs: "none", sm: "block" } }}>
                    Trial Days
                  </Box>
                  <Box sx={{ display: { xs: "block", sm: "none" } }}>Trial</Box>
                </TableCell>
              )}
              {isLeagueOrg && (
                <TableCell
                  align="center"
                  sx={{ minWidth: { xs: 80, sm: 100 } }}
                >
                  Type
                </TableCell>
              )}
              <TableCell sx={{ minWidth: { xs: 180, sm: 220 } }}>
                Email
              </TableCell>
              <TableCell sx={{ minWidth: { xs: 130, sm: 170 } }}>
                Phone
              </TableCell>
              <TableCell align="center" sx={{ minWidth: { xs: 80, sm: 100 } }}>
                <Box sx={{ display: { xs: "none", sm: "block" } }}>
                  Games Played
                </Box>
                <Box sx={{ display: { xs: "block", sm: "none" } }}>Games</Box>
              </TableCell>
              <TableCell sx={{ minWidth: { xs: 70, sm: 90 } }}>
                Status
              </TableCell>
              <TableCell sx={{ minWidth: { xs: 100, sm: 150 } }}>
                Badges
              </TableCell>
              <TableCell sx={{ minWidth: { xs: 120, sm: 180 } }}>
                Admin Note
              </TableCell>
              <TableCell align="center" sx={{ minWidth: { xs: 80, sm: 100 } }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPlayers.map((player) => (
              <TableRow key={player.id}>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    backgroundColor: "background.paper",
                    zIndex: 1,
                    minWidth: { xs: 140, sm: 180 },
                    maxWidth: { xs: 140, sm: 200 },
                    boxShadow: "2px 0 5px rgba(0,0,0,0.1)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1, sm: 2 },
                      overflow: "hidden",
                    }}
                  >
                    <SafeAvatar
                      src={player.profilePicture}
                      alt={player.name}
                      fallbackText={player.name}
                      sx={{
                        width: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        flexShrink: 0,
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        flexWrap: "wrap",
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        {player.name}
                      </Typography>
                      {player.isTournamentOnly && (
                        <Chip
                          label="Tournament Account"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: { xs: "0.6rem", sm: "0.65rem" },
                            flexShrink: 0,
                          }}
                        />
                      )}
                      {player.hasAccount ? (
                        <Tooltip title="Account linked">
                          <AccountLinkedIcon
                            sx={{
                              fontSize: { xs: 16, sm: 18 },
                              color: "success.main",
                              flexShrink: 0,
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="No account created">
                          <NoAccountIcon
                            sx={{
                              fontSize: { xs: 16, sm: 18 },
                              color: "text.disabled",
                              flexShrink: 0,
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                {isTournamentOrg && (
                  <TableCell>
                    <Chip
                      label={
                        (player as any).level !== undefined &&
                        (player as any).level !== null
                          ? `Level ${(player as any).level}`
                          : "Level N/A"
                      }
                      color={
                        (player as any).level !== undefined &&
                        (player as any).level !== null
                          ? "primary"
                          : "default"
                      }
                      size="small"
                      sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                    />
                  </TableCell>
                )}
                {isLeagueOrg && (
                  <TableCell>
                    <Chip
                      label={player.division || "unassigned"}
                      color={
                        player.division === "Master"
                          ? "error"
                          : player.division === "Division 1"
                            ? "primary"
                            : player.division === "Division 2"
                              ? "secondary"
                              : player.division === "Women Division"
                                ? "success"
                                : "default"
                      }
                      size="small"
                      sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                    />
                  </TableCell>
                )}
                {isLeagueOrg && (
                  <TableCell align="center">
                    <Box>
                      {player.divisionStats &&
                      Object.keys(player.divisionStats).length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                            alignItems: "center",
                          }}
                        >
                          {Object.entries(player.divisionStats)
                            .sort(([divA], [divB]) => divA.localeCompare(divB))
                            .map(([division, stats]) => (
                              <Chip
                                key={division}
                                label={`${division}: ${stats.totalPoints}pts`}
                                size="small"
                                color={
                                  division === player.division
                                    ? "primary"
                                    : "default"
                                }
                                variant={
                                  division === player.division
                                    ? "filled"
                                    : "outlined"
                                }
                                sx={{
                                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                                  minWidth: { xs: 100, sm: 140 },
                                }}
                              />
                            ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No stats yet
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                )}
                {isLeagueOrg && (
                  <TableCell align="center">
                    <Typography variant="body2">
                      {player.trialDaysCount || 0}
                    </Typography>
                  </TableCell>
                )}
                {isLeagueOrg && (
                  <TableCell align="center">
                    <Chip
                      label={player.isGuest ? "Guest" : "Regular"}
                      color={player.isGuest ? "warning" : "default"}
                      size="small"
                      sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                    />
                  </TableCell>
                )}
                <TableCell>
                  {player.email ? (
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: { xs: 180, sm: 220 },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={player.email}
                    >
                      {player.email}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {player.phone ? (
                    <Typography variant="body2">{player.phone}</Typography>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{player.gamesPlayed}</Typography>
                </TableCell>
                <TableCell>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      alignItems: "flex-start",
                    }}
                  >
                    <Chip
                      label={player.gamesPlayed > 0 ? "Active" : "Inactive"}
                      color={player.gamesPlayed > 0 ? "success" : "error"}
                      size="small"
                      sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                    />
                    {player.isBanned && (
                      <Chip
                        label="Banned"
                        color="error"
                        size="small"
                        icon={<BlockIcon />}
                        sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <BadgeDisplay badges={player.badges} size="small" />
                </TableCell>
                <TableCell>
                  {player.adminNote ? (
                    <Typography
                      variant="caption"
                      sx={{
                        fontStyle: "italic",
                        color: "text.secondary",
                        display: "block",
                        maxWidth: { xs: 120, sm: 180 },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={player.adminNote}
                    >
                      {player.adminNote}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: "flex", gap: { xs: 0, sm: 1 } }}>
                    <IconButton
                      onClick={() => handleEditPlayer(player)}
                      size="small"
                      title="Edit Player"
                    >
                      <EditIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenMoveDialog(player)}
                      size="small"
                      color="primary"
                      title="Move to Different Division"
                    >
                      <SwapHorizIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    </IconButton>
                    {player.hasAccount && (
                      <Tooltip title="Login as player (admin impersonation)">
                        <IconButton
                          onClick={() => handleImpersonate(player)}
                          size="small"
                          color="secondary"
                        >
                          <ImpersonateIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {player.hasAccount && (
                      <IconButton
                        onClick={() => handleBanToggle(player)}
                        size="small"
                        color={player.isBanned ? "success" : "warning"}
                        title={player.isBanned ? "Unban Player" : "Ban Player"}
                      >
                        {player.isBanned ? (
                          <CheckIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        ) : (
                          <BlockIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        )}
                      </IconButton>
                    )}
                    {player.hasAccount && (
                      <IconButton
                        onClick={() => handleOpenIpDialog(player)}
                        size="small"
                        color={player.autoBlockIp ? "error" : "default"}
                        title="View / Block Login IPs"
                      >
                        <IpIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                      </IconButton>
                    )}
                    {activeSeason && player.currentSeason !== activeSeason.id && (
                      <IconButton
                        onClick={() => handleOpenApproveSeasonDialog(player)}
                        size="small"
                        color="success"
                        title={`Approve for ${activeSeason.name}`}
                      >
                        <HowToRegIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                      </IconButton>
                    )}
                    <IconButton
                      onClick={() => handleDeletePlayer(player)}
                      size="small"
                      color="error"
                      title="Delete Player"
                    >
                      <DeleteIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenMergeDialog(player)}
                      size="small"
                      color="info"
                      title="Merge Player Profiles"
                    >
                      <MergePlayersIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingPlayer ? "Edit Player" : "Add New Player"}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert
              severity="error"
              sx={{ mt: 2, mb: 2 }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          <TextField
            autoFocus
            margin="dense"
            label="Player Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          {/* Level field for tournament and both type orgs */}
          {isTournamentOrg && (
            <TextField
              margin="dense"
              label="Level"
              fullWidth
              variant="outlined"
              type="number"
              value={formData.level}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  level: parseFloat(e.target.value) || 0,
                })
              }
              helperText="Player skill level (e.g., 1.0 - 7.0)"
              inputProps={{ min: 0, max: 10, step: 0.5 }}
            />
          )}

          {/* Division field for league and both type orgs */}
          {isLeagueOrg && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Division</InputLabel>
              <Select
                value={formData.division}
                label="Division"
                disabled={!!editingPlayer}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    division: e.target.value as Division,
                  })
                }
              >
                <MenuItem value="unassigned">📋 Unassigned</MenuItem>
                <MenuItem value="Master">🏆 Master</MenuItem>
                <MenuItem value="Division 1">🥇 Division 1</MenuItem>
                <MenuItem value="Division 2">🥈 Division 2</MenuItem>
                <MenuItem value="Division 2A">🥈 Division 2A</MenuItem>
                <MenuItem value="Division 2B">🥈 Division 2B</MenuItem>
                <MenuItem value="Division 3">🥉 Division 3</MenuItem>
                <MenuItem value="Division 4">🎾 Division 4</MenuItem>
                <MenuItem value="Women Division">👩 Women Division 1</MenuItem>
                <MenuItem value="Women Division 2">
                  👩 Women Division 2
                </MenuItem>
                <MenuItem value="Test">🧪 Test</MenuItem>
              </Select>
              {editingPlayer && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  To change division, use the "Move to Different Division"
                  button in the players table
                </Typography>
              )}
            </FormControl>
          )}

          {/* Trial days and guest player fields for league and both type orgs */}
          {isLeagueOrg && (
            <>
              <TextField
                margin="dense"
                label="Trial Days Attended"
                fullWidth
                variant="outlined"
                type="number"
                value={formData.trialDaysCount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    trialDaysCount: Math.max(0, parseInt(e.target.value) || 0),
                  })
                }
                helperText="Number of trial days this player attended (for finance calculations)"
                inputProps={{ min: 0, max: 10 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isGuest}
                    onChange={(e) =>
                      setFormData({ ...formData, isGuest: e.target.checked })
                    }
                  />
                }
                label="Guest Player (€20 per league day from day 1)"
                sx={{ mt: 2, mb: 1 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                }
                label="Active Player (Can register for league days and play)"
                sx={{ mb: 1 }}
              />
            </>
          )}

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Profile Picture
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Avatar
                src={
                  imageFile
                    ? URL.createObjectURL(imageFile)
                    : formData.profilePicture
                }
                alt={formData.name}
                sx={{ width: 60, height: 60 }}
              >
                {formData.name.charAt(0).toUpperCase()}
              </Avatar>

              <Box>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="profile-picture-upload"
                  type="file"
                  onChange={handleImageChange}
                />
                <Button
                  variant="outlined"
                  onClick={handlePhotoMenuOpen}
                  startIcon={<PhotoCameraIcon />}
                  disabled={imageUploading}
                >
                  {imageFile ? "Change Photo" : "Upload Photo"}
                </Button>

                <Menu
                  anchorEl={photoMenuAnchor}
                  open={Boolean(photoMenuAnchor)}
                  onClose={handlePhotoMenuClose}
                >
                  <MenuItem onClick={handleOpenCamera}>
                    <ListItemIcon>
                      <CameraAltIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Take Photo</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleUploadFromDevice}>
                    <ListItemIcon>
                      <UploadIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Upload from Device</ListItemText>
                  </MenuItem>
                </Menu>

                {(formData.profilePicture || imageFile) && (
                  <Button
                    variant="text"
                    color="error"
                    size="small"
                    sx={{ ml: 1 }}
                    onClick={() => {
                      setImageFile(null);
                      setFormData({ ...formData, profilePicture: "" });
                    }}
                    disabled={imageUploading}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            </Box>

            {imageUploading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Uploading image...
                </Typography>
              </Box>
            )}

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Max file size: 2MB. Recommended: Square images work best.
            </Typography>
            <Typography
              variant="caption"
              color="primary"
              display="block"
              sx={{ mt: 0.5 }}
            >
              💡 Tip: Copy an image and press Ctrl+V (or Cmd+V) to paste it here
            </Typography>
          </Box>

          {/* SP Team Badge Management */}
          {editingPlayer && (
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Special Badges
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasSpTeamBadge}
                    onChange={(e) => setHasSpTeamBadge(e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>🏆</span>
                    <Typography variant="body2">
                      SP Team - Stuttgart Padel League family & friends
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isManagementTeam}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        isManagementTeam: e.target.checked,
                      }))
                    }
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>⚙️</span>
                    <Typography variant="body2">
                      SP Team member (shows "SP Team" badge on leaderboard + auto-registers for league days)
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.autoRegisterLeagueDay}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        autoRegisterLeagueDay: e.target.checked,
                      }))
                    }
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>📅</span>
                    <Typography variant="body2">
                      Auto-register for league days (no SP Team badge)
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Assign this badge to family and friends of the organizers
              </Typography>
            </Box>
          )}

          {/* Extra Division Access */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Extra Division Access
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Can also register for league days in…</InputLabel>
              <Select
                multiple
                value={formData.allowedDivisions || []}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    allowedDivisions: e.target.value as string[],
                  }))
                }
                input={<OutlinedInput label="Can also register for league days in…" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {((selected as string[]) || []).map((val) => (
                      <Chip key={val} label={val} size="small" />
                    ))}
                  </Box>
                )}
              >
                {availableDivisions
                  .filter((div) => div !== formData.division)
                  .map((div) => (
                    <MenuItem key={div} value={div}>
                      <Checkbox checked={(formData.allowedDivisions || []).includes(div)} />
                      <ListItemText primary={div} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Player will see and can register for league days in these additional divisions
            </Typography>
          </Box>

          {/* Division Movement History */}
          {editingPlayer &&
            editingPlayer.divisionHistory &&
            editingPlayer.divisionHistory.length > 0 && (
              <Box sx={{ mt: 3, mb: 2 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <HistoryIcon color="action" />
                  <Typography variant="subtitle2">
                    Division Movement History
                  </Typography>
                </Box>

                <Box
                  sx={{
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    p: 2,
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                >
                  <Timeline sx={{ p: 0, m: 0 }}>
                    {editingPlayer.divisionHistory
                      .slice()
                      .sort((a, b) => {
                        const dateA = new Date(a.movedAt);
                        const dateB = new Date(b.movedAt);
                        return dateB.getTime() - dateA.getTime();
                      })
                      .map((movement, index) => {
                        const movedDate = new Date(movement.movedAt);

                        return (
                          <TimelineItem key={index}>
                            <TimelineOppositeContent
                              sx={{
                                m: "auto 0",
                                flex: 0.3,
                                textAlign: "right",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {movedDate.toLocaleDateString("en-GB", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </Typography>
                            </TimelineOppositeContent>
                            <TimelineSeparator>
                              <TimelineConnector
                                sx={{
                                  bgcolor:
                                    index === 0 ? "transparent" : "grey.400",
                                }}
                              />
                              <TimelineDot
                                color={index === 0 ? "primary" : "grey"}
                              >
                                <SwapHorizIcon sx={{ fontSize: 16 }} />
                              </TimelineDot>
                              <TimelineConnector
                                sx={{
                                  bgcolor:
                                    index ===
                                    (editingPlayer.divisionHistory?.length ||
                                      0) -
                                      1
                                      ? "transparent"
                                      : "grey.400",
                                }}
                              />
                            </TimelineSeparator>
                            <TimelineContent sx={{ py: "12px", px: 2 }}>
                              <Typography
                                variant="body2"
                                component="span"
                                fontWeight="medium"
                              >
                                {movement.fromDivision} → {movement.toDivision}
                              </Typography>
                              {movement.reason && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                  sx={{ mt: 0.5 }}
                                >
                                  {movement.reason}
                                </Typography>
                              )}
                              {movement.movedBy && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                  sx={{ mt: 0.25 }}
                                >
                                  By: {movement.movedBy}
                                </Typography>
                              )}
                            </TimelineContent>
                          </TimelineItem>
                        );
                      })}
                  </Timeline>
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 1 }}
                >
                  📊 Stats from each division are preserved and can be viewed in
                  the Finances tab
                </Typography>
              </Box>
            )}

          {/* Admin Note */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <TextField
              label="Admin Note"
              multiline
              rows={3}
              fullWidth
              value={formData.adminNote}
              onChange={(e) =>
                setFormData({ ...formData, adminNote: e.target.value })
              }
              placeholder="Add internal notes about this player (e.g., special requests, contact preferences, payment status, etc.)"
              helperText="This note is only visible to admins"
              variant="outlined"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={imageUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePlayer}
            variant="contained"
            disabled={!formData.name.trim() || imageUploading}
          >
            {imageUploading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : editingPlayer ? (
              "Update"
            ) : (
              "Add"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Camera Capture Dialog */}
      <Dialog
        open={cameraDialogOpen}
        onClose={handleCloseCameraDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Take Photo</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: 480,
                aspectRatio: "4/3",
                bgcolor: "black",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
            <canvas ref={canvasRef} style={{ display: "none" }} />
            <Typography variant="caption" color="text.secondary">
              Position your face in the center and click "Capture"
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCameraDialog}>Cancel</Button>
          <Button
            onClick={handleCapturePhoto}
            variant="contained"
            startIcon={<PhotoCameraIcon />}
          >
            Capture
          </Button>
        </DialogActions>
      </Dialog>

      <MergePlayersDialog
        open={mergeDialogOpen}
        targetPlayer={playerToMerge}
        onClose={() => {
          setMergeDialogOpen(false);
          setPlayerToMerge(null);
        }}
        onMerged={handleMergeSuccess}
      />

      {/* Move Player Dialog */}
      <Dialog
        open={movePlayerDialogOpen}
        onClose={() => setMovePlayerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Move Player to Different Division</DialogTitle>
        <DialogContent>
          {playerToMove && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Moving: <strong>{playerToMove.name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current Division: <strong>{playerToMove.division}</strong>
                </Typography>
              </Alert>

              {/* Show current stats */}
              {playerToMove.divisionStats?.[playerToMove.division] && (
                <Box
                  sx={{ mb: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Stats in {playerToMove.division}:
                  </Typography>
                  <Typography variant="body2">
                    Points:{" "}
                    {
                      playerToMove.divisionStats[playerToMove.division]
                        .totalPoints
                    }{" "}
                    | Games:{" "}
                    {
                      playerToMove.divisionStats[playerToMove.division]
                        .gamesPlayed
                    }{" "}
                    | Matches:{" "}
                    {
                      playerToMove.divisionStats[playerToMove.division]
                        .matchesWon
                    }
                    W-
                    {
                      playerToMove.divisionStats[playerToMove.division]
                        .matchesLost
                    }
                    L | League Days:{" "}
                    {
                      playerToMove.divisionStats[playerToMove.division]
                        .leagueDaysPlayed
                    }
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    These stats will be preserved in {playerToMove.division}
                  </Typography>
                </Box>
              )}

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Target Division</InputLabel>
                <Select
                  value={targetDivision}
                  onChange={(e) =>
                    setTargetDivision(e.target.value as Division)
                  }
                  label="Target Division"
                >
                  {availableDivisions.map((div) => (
                    <MenuItem key={div} value={div}>
                      {div}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Reason (Optional)"
                value={moveReason}
                onChange={(e) => setMoveReason(e.target.value)}
                multiline
                rows={2}
                placeholder="e.g., Player skill level improved, requested by player, etc."
                sx={{ mb: 2 }}
              />

              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Important:</strong>
                  <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                    <li>
                      Player must NOT be in any active or upcoming league days
                    </li>
                    <li>
                      Historical stats in {playerToMove.division} will be
                      preserved
                    </li>
                    <li>
                      Future matches will count towards{" "}
                      {targetDivision || "the new division"}
                    </li>
                    <li>The player will start fresh in the new division</li>
                  </ul>
                </Typography>
              </Alert>

              {moveError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {moveError}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMovePlayerDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleMovePlayer}
            variant="contained"
            disabled={!targetDivision || moveLoading}
            startIcon={moveLoading && <CircularProgress size={20} />}
          >
            {moveLoading ? "Moving..." : "Move Player"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve for Season Dialog */}
      <Dialog
        open={approveSeasonDialogOpen}
        onClose={() => setApproveSeasonDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Approve Player for Season</DialogTitle>
        <DialogContent>
          {playerToApprove && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  Player: <strong>{playerToApprove.name}</strong>
                </Typography>
                <Typography variant="body2">
                  This will create an approved registration and link them to the selected season.
                </Typography>
              </Alert>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Season</InputLabel>
                <Select
                  value={approveSeasonId}
                  label="Season"
                  onChange={(e) => setApproveSeasonId(e.target.value)}
                >
                  {allSeasons.map((season) => (
                    <MenuItem key={season.id} value={season.id}>
                      {season.name}{season.isActive ? " (active)" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {approveError && (
                <Alert severity="error">{approveError}</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveSeasonDialogOpen(false)} disabled={approveLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleApproveForSeason}
            variant="contained"
            color="success"
            disabled={!approveSeasonId || approveLoading}
            startIcon={approveLoading ? <CircularProgress size={18} /> : <HowToRegIcon />}
          >
            {approveLoading ? "Approving..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ban/Unban Player Dialog */}
      <Dialog
        open={banDialogOpen}
        onClose={() => setBanDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {playerToBan?.isBanned ? "Unban Player" : "Ban Player"}
        </DialogTitle>
        <DialogContent>
          {playerToBan && (
            <Box sx={{ mt: 2 }}>
              <Alert
                severity={playerToBan.isBanned ? "info" : "warning"}
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Player: <strong>{playerToBan.name}</strong>
                </Typography>
                {playerToBan.isBanned ? (
                  <Typography variant="body2">
                    This will allow the player to register for league days,
                    tournaments, and seasons again.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" gutterBottom>
                      This will prevent the player from:
                    </Typography>
                    <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                      <li>Registering for league days</li>
                      <li>Registering for tournaments</li>
                      <li>Registering for next season</li>
                    </ul>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      The player will still be able to:
                    </Typography>
                    <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                      <li>View their stats and history</li>
                      <li>Request account deletion</li>
                      <li>Download their personal data (GDPR)</li>
                    </ul>
                  </>
                )}
              </Alert>

              {!playerToBan.isBanned && (
                <TextField
                  fullWidth
                  label="Ban Reason (Optional)"
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  multiline
                  rows={3}
                  placeholder="e.g., Violation of community rules, repeated no-shows, etc."
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBanDialogOpen(false)} disabled={banLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleBanConfirm}
            variant="contained"
            color={playerToBan?.isBanned ? "success" : "warning"}
            disabled={banLoading}
            startIcon={banLoading && <CircularProgress size={20} />}
          >
            {banLoading
              ? playerToBan?.isBanned
                ? "Unbanning..."
                : "Banning..."
              : playerToBan?.isBanned
                ? "Unban Player"
                : "Ban Player"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login IP History / Block Dialog */}
      <Dialog
        open={ipDialogOpen}
        onClose={() => setIpDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Login IPs — {playerForIps?.name}</DialogTitle>
        <DialogContent>
          <Alert severity={playerForIps?.autoBlockIp ? "error" : "info"} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!playerForIps?.autoBlockIp}
                  disabled={autoBlockLoading}
                  onChange={handleToggleAutoBlock}
                  color="error"
                />
              }
              label="Auto-block this account (IP + device)"
            />
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {playerForIps?.autoBlockIp
                ? "Enabled: any login attempt on this account is denied, and the IP and browser used are blocked immediately — including ones not seen before. Also catches this browser browsing anonymously without logging in. No manual blocking needed."
                : "When enabled, this account can no longer log in from any IP or browser, current or future — each attempt is denied and blocked automatically. Also blocks that browser from browsing the site at all, even without logging in."}
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            IPs this account has logged in from, most recent first. Blocking an
            IP individually below takes effect immediately and applies
            site-wide (not just to this player). Manage all blocked IPs and
            devices from the Blocked IPs admin page.
          </Typography>
          {!playerForIps?.loginIpHistory?.length && (
            <Typography variant="body2" color="text.secondary">
              No login IPs recorded yet.
            </Typography>
          )}
          {(playerForIps?.loginIpHistory || []).map((entry) => (
            <Box
              key={entry.ip}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {entry.ip}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Last used: {new Date(entry.at).toLocaleString()}
                </Typography>
              </Box>
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={blockingIp === entry.ip}
                startIcon={
                  blockingIp === entry.ip ? (
                    <CircularProgress size={16} />
                  ) : (
                    <BlockIcon fontSize="small" />
                  )
                }
                onClick={() => handleBlockIp(entry.ip)}
              >
                {blockingIp === entry.ip ? "Blocking..." : "Block"}
              </Button>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIpDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Players;
